import { Box, Button, Group, Select, Stack, Text, TextInput } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconSearch } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DataTable,
  EmptyState,
  ErrorState,
  ExportMenu,
  LoadingSkeleton,
  SectionCard,
  StateBadge,
  useColumns,
  useSlashToFocus,
} from "../../components/ui";
import { Page } from "../../shell/Page";
import { useAuth } from "../../auth/AuthContext";
import { api, type ManagedUser, type Role } from "../../lib/api";
import { roleLabel } from "../../lib/roleLabels";


/** True while a password account is inside its lockout window. */
function isLockedOut(user: ManagedUser): boolean {
  return user.lockedUntil !== null && new Date(user.lockedUntil) > new Date();
}

export function UserList() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<ManagedUser[]>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashToFocus(searchRef);

  async function load() {
    try {
      setError(undefined);
      const { users: loaded } = await api.adminUsers({
        ...(roleFilter ? { role: roleFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(query.trim() ? { query: query.trim() } : {}),
      });
      setUsers(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load users.");
    }
  }

  useEffect(() => {
    void (async () => {
      try {
        const meta = await api.adminMeta();
        setRoles(meta.roles);
      } catch {
        // The filter just falls back to "all roles"; not worth a banner.
      }
    })();
  }, []);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter]);

  async function unlock(user: ManagedUser) {
    setBusyId(user.id);
    setError(undefined);
    try {
      await api.unlockUser(user.id);
      setNotice(`${user.name} can try their password again.`);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not unlock.");
    } finally {
      setBusyId(undefined);
    }
  }

  async function setStatus(user: ManagedUser, status: string) {
    setBusyId(user.id);
    setError(undefined);
    try {
      await api.updateAdminUser(user.id, { status });
      setNotice(
        status === "ACTIVE"
          ? `${user.name} can sign in again.`
          : `${user.name} can no longer sign in.`,
      );
      await load();
    } catch (caught) {
      // The refusals here are deliberate and explain themselves - the last
      // active Admin, or your own account - so show the server's words.
      setError(caught instanceof Error ? caught.message : "Could not change that.");
    } finally {
      setBusyId(undefined);
    }
  }

  const active = users?.filter((u) => u.status === "ACTIVE").length ?? 0;

  /**
   * Deactivating an account stops that person signing in at all. The server
   * refuses it for your own account and so does the button, but for anybody
   * else it is worth stating what it does before it happens - "Deactivate"
   * next to a name reads like tidying a list.
   */
  function confirmStatus(user: ManagedUser, next: "ACTIVE" | "INACTIVE") {
    modals.openConfirmModal({
      title: next === "INACTIVE" ? `Deactivate ${user.name}` : `Reactivate ${user.name}`,
      children: (
        <Text fz="md">
          {next === "INACTIVE"
            ? "They will not be able to sign in. Everything they have already recorded stays exactly as it is, and their name stays on it."
            : "They will be able to sign in again with the method already on their account."}
        </Text>
      ),
      labels: {
        confirm: next === "INACTIVE" ? "Deactivate" : "Reactivate",
        cancel: "Cancel",
      },
      ...(next === "INACTIVE" ? { confirmProps: { color: "red" } } : {}),
      onConfirm: () => void setStatus(user, next),
    });
  }

  /** How this person signs in, in one line. */
  function signsIn(user: ManagedUser): string {
    return user.username
      ? `${user.username} · password`
      : `${user.mobileNumber ?? "—"} · mobile code`;
  }

  const columns = useColumns<ManagedUser>(
    () => [
      {
        key: "Person",
        header: "Person",
        width: "auto",
        render: (user) => (
          <Group gap={6} wrap="nowrap">
            <Text fz={14} fw={500} truncate style={{ minWidth: 0 }}>
              {user.name}
            </Text>
            {user.id === me?.id && <StateBadge state="svcDiagnosed" label="You" />}
            {user.status !== "ACTIVE" && (
              <StateBadge state="svcRejected" label="Deactivated" />
            )}
            {isLockedOut(user) && (
              <StateBadge
                state="dlvPending"
                label={`Locked out until ${new Date(
                  user.lockedUntil!,
                ).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`}
              />
            )}
            {user.mustChangePassword && (
              <StateBadge state="svcReceived" label="Must set a new password" />
            )}
          </Group>
        ),
      },
      {
        key: "Roles",
        header: "Roles",
        width: "320px",
        // Every role, joined - not the primary one. One person holds several,
        // and showing one would make this list disagree with the form behind
        // it, which is the whole reason roles became many.
        render: (user) => (
          <Text fz={13} c="gray.7" truncate>
            {user.roles.length > 0
              ? user.roles.map(roleLabel).join(" · ")
              : roleLabel(user.role)}
          </Text>
        ),
      },
      {
        key: "How they sign in",
        header: "How they sign in",
        width: "250px",
        render: (user) => (
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text fz={13} className="num" c="gray.7" truncate>
              {signsIn(user)}
            </Text>
            {isLockedOut(user) && (
              <Text fz={12} c="gray.6">
                {user.failedLoginAttempts} failed attempts
              </Text>
            )}
          </Stack>
        ),
        value: signsIn,
      },
      {
        key: "action",
        header: "",
        width: "230px",
        render: (user) => (
          <Group gap={6} wrap="nowrap" justify="flex-end">
            {isLockedOut(user) && (
              <Button
                variant="default"
                size="compact-sm"
                loading={busyId === user.id}
                onClick={(event) => {
                  event.stopPropagation();
                  void unlock(user);
                }}
              >
                Unlock
              </Button>
            )}
            {user.status === "ACTIVE" ? (
              <Button
                variant="default"
                size="compact-sm"
                // Refused by the server for your own account too; there is no
                // reason to offer a button that cannot work.
                disabled={busyId === user.id || user.id === me?.id}
                onClick={(event) => {
                  event.stopPropagation();
                  confirmStatus(user, "INACTIVE");
                }}
              >
                Deactivate
              </Button>
            ) : (
              <Button
                variant="default"
                size="compact-sm"
                disabled={busyId === user.id}
                onClick={(event) => {
                  event.stopPropagation();
                  confirmStatus(user, "ACTIVE");
                }}
              >
                Reactivate
              </Button>
            )}
          </Group>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me?.id, busyId],
  );

  return (
    <Page
      title="Users"
      subtitle={users ? `${active} active of ${users.length} shown` : undefined}
      back="/admin"
      width="wide"
      actions={
        <Button component={Link} to="/admin/users/new" size="sm">
          Add user
        </Button>
      }
    >
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {notice && (
        <Text fz="md" c="teal.8">
          {notice}
        </Text>
      )}

      <SectionCard title="Find an account">
        <Stack gap="sm">
          <Group gap="sm" align="flex-end" wrap="nowrap">
            <TextInput
              ref={searchRef}
              id="user-search"
              label="Search"
              type="search"
              value={query}
              placeholder="Name, mobile or username"
              leftSection={<IconSearch size={16} stroke={1.8} />}
              style={{ flex: 1 }}
              onChange={(event) => setQuery(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void load();
              }}
            />
            <Button variant="default" onClick={() => void load()}>
              Find
            </Button>
          </Group>

          <Select
            id="role-filter"
            label="Role"
            value={roleFilter}
            onChange={(value) => setRoleFilter(value ?? "")}
            allowDeselect={false}
            searchable
            data={[
              { value: "", label: "All roles" },
              ...roles.map((role) => ({ value: role, label: roleLabel(role) })),
            ]}
          />
          <Select
            id="status-filter"
            label="Status"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value ?? "")}
            allowDeselect={false}
            data={[
              { value: "", label: "Active and inactive" },
              { value: "ACTIVE", label: "Active only" },
              { value: "INACTIVE", label: "Inactive only" },
            ]}
          />
        </Stack>
      </SectionCard>

      {!users && !error && <LoadingSkeleton variant="card" count={5} />}
      {users?.length === 0 && <EmptyState title="No accounts match that" />}

      {/* Desktop: the brief's table. Every row state it names is a chip in
          the Person column rather than a colour - "locked out" and
          "deactivated" are facts somebody has to be able to read, and a
          tinted row says neither of them out loud. */}
      {users && users.length > 0 && (
        <Box visibleFrom="md">
          <Stack gap={12}>
            <Group justify="flex-end">
              <ExportMenu dataset="users" />
            </Group>
            <DataTable
              rows={users}
              columns={columns}
              rowKey={(user) => user.id}
              onOpen={(user) => navigate(`/admin/users/${user.id}`)}
              caption={`Users: ${users.length}`}
            />
            <Text fz={12} c="gray.6">
              j / k or the arrow keys move, Enter opens the person. One person,
              one account, every role they do.
            </Text>
          </Stack>
        </Box>
      )}

      {/* Phone: the same people as cards. */}
      <Box hiddenFrom="md">
      <Stack gap="md">
      {users?.map((user) => {
        const locked = isLockedOut(user);
        const isMe = user.id === me?.id;
        return (
          <SectionCard
            key={user.id}
            title={user.name}
            action={
              <Button
                component={Link}
                to={`/admin/users/${user.id}`}
                variant="default"
                size="sm"
              >
                Edit
              </Button>
            }
          >
            <Stack gap="sm">
              <Group gap="xs" wrap="wrap">
                {isMe && <StateBadge state="svcDiagnosed" label="You" />}
                {user.status !== "ACTIVE" && (
                  <StateBadge state="svcRejected" label="Deactivated — cannot sign in" />
                )}
                {locked && (
                  <StateBadge
                    state="dlvPending"
                    label={`Locked out until ${new Date(
                      user.lockedUntil!,
                    ).toLocaleTimeString()}`}
                  />
                )}
                {user.mustChangePassword && (
                  <StateBadge state="svcReceived" label="Must set a new password" />
                )}
              </Group>

              {/* Every role, not the primary one: the whole point of the
                  change is that one person holds several, and showing one
                  would make the list disagree with the form. */}
              <Stack gap={2}>
                <Text fz="md" c="gray.7">
                  {user.roles.length > 0
                    ? user.roles.map(roleLabel).join(" · ")
                    : roleLabel(user.role)}
                </Text>
                <Text fz="sm" c="gray.6" className="num">
                  {user.username
                    ? `${user.username} · username and password`
                    : `${user.mobileNumber} · mobile code`}
                </Text>
                {locked && (
                  <Text fz="sm" c="gray.6">
                    Locked after {user.failedLoginAttempts} failed attempts.
                  </Text>
                )}
              </Stack>

              <Group gap="sm">
                {locked && (
                  <Button
                    variant="default"
                    size="sm"
                    loading={busyId === user.id}
                    onClick={() => void unlock(user)}
                  >
                    Unlock
                  </Button>
                )}
                {user.status === "ACTIVE" ? (
                  <Button
                    variant="default"
                    size="sm"
                    // Disabled for your own account: the server refuses it
                    // too, but there is no reason to offer a button that
                    // cannot work.
                    disabled={busyId === user.id || isMe}
                    onClick={() => confirmStatus(user, "INACTIVE")}
                  >
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={busyId === user.id}
                    onClick={() => confirmStatus(user, "ACTIVE")}
                  >
                    Reactivate
                  </Button>
                )}
              </Group>
            </Stack>
          </SectionCard>
        );
      })}
      </Stack>
      </Box>
    </Page>
  );
}
